import { useCallback } from 'react';

import { type BuildQuizListItem } from '../../../form/schema/BuildQuizListSchema';
import { AI_BUILDER_CHAT_PLACEHOLDER } from '../constants';
import {
  appendSubmittedAiBuilderConfirmUserMessage,
  createSubmittedAiBuilderConfirmUserMessageId,
  getAiBuilderConfirmMessageIntent,
} from '../store/aiBuilderMessageState';
import {
  createAiBuilderClientRequestId,
  createConfirmTurnRequest,
} from '../store/aiBuilderTurnRequests';
import { isAiBuilderUsageBlocked } from '../store/aiBuilderUsagePolicy';
import {
  type AiBuilderChatActions,
  type AiBuilderChatState,
  type AiBuilderSubmitPromptOptions,
  type AiBuilderUserMessageRetryRequest,
} from '../store/types';
import { type useDispatchServerTurn } from '../store/useAiBuilderServerTurnDispatcher';
import { aiBuilderChatAdapter } from '../utils/aiBuilderChatAdapter';
import { type AiBuilderChatCopy } from '../utils/aiBuilderChatCopy';
import { trackAiBuilderUsageBlocked } from '../utils/aiBuilderChatTracking';
import { type AiBuilderTurnRequest } from '../utils/types';

type UpdateAiBuilderChatState = (
  updater: (prev: AiBuilderChatState) => AiBuilderChatState,
) => void;
type AiBuilderMutableRef<T> = { current: T };
type GetNextAiBuilderRequestId = () => number;
type IsAiBuilderTurnBusy = () => boolean;

type CreateTurnContextParams = {
  signal?: AbortSignal;
  sourceRefs?: string[];
  turnHints?: AiBuilderSubmitPromptOptions['turnHints'];
};

type UseAiBuilderSubmitConfirmParams = {
  copy: AiBuilderChatCopy;
  createTurnContext: (
    params?: CreateTurnContextParams,
  ) => AiBuilderTurnRequest['context'];
  dispatchServerTurn: ReturnType<typeof useDispatchServerTurn>;
  isTurnBusy: IsAiBuilderTurnBusy;
  lastQuizListSnapshotRef: AiBuilderMutableRef<BuildQuizListItem[] | null>;
  getChatState: () => AiBuilderChatState;
  getNextRequestId: GetNextAiBuilderRequestId;
  updateState: UpdateAiBuilderChatState;
};

export const useAiBuilderSubmitConfirm = ({
  copy,
  createTurnContext,
  dispatchServerTurn,
  getNextRequestId,
  isTurnBusy,
  lastQuizListSnapshotRef,
  getChatState,
  updateState,
}: UseAiBuilderSubmitConfirmParams) => {
  return useCallback<AiBuilderChatActions['submitConfirmMessage']>(
    (messageId, decision) => {
      if (isTurnBusy()) return;

      const confirmIntent = getAiBuilderConfirmMessageIntent({
        decision,
        messages: getChatState().messages,
        messageId,
      });

      if (!confirmIntent) return;

      const usage = getChatState().usage;
      if (isAiBuilderUsageBlocked(usage)) {
        trackAiBuilderUsageBlocked(usage, 'confirm');
        return;
      }

      const requestId = getNextRequestId();
      const clientRequestId = createAiBuilderClientRequestId();
      const userMessageId =
        createSubmittedAiBuilderConfirmUserMessageId(requestId);
      const retryRequest: AiBuilderUserMessageRetryRequest = {
        clientRequestId,
        type: 'confirm',
        token: confirmIntent.token,
        decision: confirmIntent.decision,
      };

      updateState(prev => ({
        ...prev,
        draft: '',
        selectedFile: null,
        placeholder: AI_BUILDER_CHAT_PLACEHOLDER.default,
        messages: appendSubmittedAiBuilderConfirmUserMessage({
          messages: prev.messages,
          messageId,
          requestId,
          retry: retryRequest,
          label: confirmIntent.label,
        }),
      }));
      lastQuizListSnapshotRef.current = null;

      dispatchServerTurn(
        aiBuilderChatAdapter.startTurn,
        signal =>
          createConfirmTurnRequest({
            requestId,
            clientRequestId,
            clientCopy: copy,
            token: confirmIntent.token,
            decision: confirmIntent.decision,
            context: createTurnContext({ signal }),
          }),
        { userMessageId },
      );
    },
    [
      copy,
      createTurnContext,
      dispatchServerTurn,
      getChatState,
      getNextRequestId,
      isTurnBusy,
      lastQuizListSnapshotRef,
      updateState,
    ],
  );
};
